import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { useAdmin } from '../context/AdminContext';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5001';

const AdminAreaReports = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { getAuthHeaders, adminLogout } = useAdmin();

  const [title, setTitle] = useState('Area Safety Report');
  const [locationQuery, setLocationQuery] = useState('');
  const [centerLat, setCenterLat] = useState('');
  const [centerLng, setCenterLng] = useState('');
  const [radiusMeters, setRadiusMeters] = useState('1000');
  const [startDateLocal, setStartDateLocal] = useState('');
  const [endDateLocal, setEndDateLocal] = useState('');

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [preview, setPreview] = useState(null);

  const [savedReports, setSavedReports] = useState([]);
  const [selectedSaved, setSelectedSaved] = useState(null);

  const reportId = searchParams.get('id');

  const numericInputs = useMemo(() => {
    const lat = Number(centerLat);
    const lng = Number(centerLng);
    const radius = Number(radiusMeters);
    return {
      lat,
      lng,
      radius,
      valid: Number.isFinite(lat) && Number.isFinite(lng) && Number.isFinite(radius) && radius > 0
    };
  }, [centerLat, centerLng, radiusMeters]);

  const handleGeocode = async () => {
    const q = (locationQuery || '').trim();
    if (!q) {
      setError('Please enter a city or location name.');
      return;
    }
    setError('');
    setLoading(true);
    try {
      const res = await axios.get(`${API_URL}/api/admin/geocode`, {
        headers: getAuthHeaders(),
        params: { q }
      });
      if (res.data && typeof res.data.lat === 'number' && typeof res.data.lng === 'number') {
        setCenterLat(String(res.data.lat));
        setCenterLng(String(res.data.lng));
      }
    } catch (err) {
      if (err.response?.status === 401 || err.response?.status === 403) {
        adminLogout();
        navigate('/admin/login');
        return;
      }
      setError(err.response?.data?.message || 'Failed to find that location.');
    } finally {
      setLoading(false);
    }
  };

  const fetchSavedReports = async () => {
    setError('');
    setLoading(true);
    try {
      const res = await axios.get(`${API_URL}/api/admin/area-reports`, {
        headers: getAuthHeaders()
      });
      setSavedReports(res.data.reports || []);
    } catch (err) {
      if (err.response?.status === 401 || err.response?.status === 403) {
        adminLogout();
        navigate('/admin/login');
        return;
      }
      setError(err.response?.data?.message || 'Failed to load saved area reports');
    } finally {
      setLoading(false);
    }
  };

  const fetchSavedReport = async (id) => {
    if (!id) return;
    setError('');
    setLoading(true);
    try {
      const res = await axios.get(`${API_URL}/api/admin/area-reports/${id}`, {
        headers: getAuthHeaders()
      });
      setSelectedSaved(res.data || null);
    } catch (err) {
      if (err.response?.status === 401 || err.response?.status === 403) {
        adminLogout();
        navigate('/admin/login');
        return;
      }
      setError(err.response?.data?.message || 'Failed to load report');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSavedReports();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!reportId) {
      setSelectedSaved(null);
      return;
    }
    fetchSavedReport(reportId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reportId]);

  const handlePreview = async () => {
    if (!numericInputs.valid) {
      setError('Please enter valid center latitude/longitude and radius.');
      return;
    }

    setError('');
    setLoading(true);
    try {
      const startDate = startDateLocal ? new Date(startDateLocal).toISOString() : undefined;
      const endDate = endDateLocal ? new Date(endDateLocal).toISOString() : undefined;
      const res = await axios.post(`${API_URL}/api/admin/area-reports/preview`, {
        centerLat: numericInputs.lat,
        centerLng: numericInputs.lng,
        radiusMeters: numericInputs.radius,
        startDate,
        endDate
      }, {
        headers: getAuthHeaders()
      });
      setPreview(res.data);
    } catch (err) {
      if (err.response?.status === 401 || err.response?.status === 403) {
        adminLogout();
        navigate('/admin/login');
        return;
      }
      setError(err.response?.data?.message || 'Failed to generate preview');
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async () => {
    if (!numericInputs.valid) {
      setError('Please enter valid center latitude/longitude and radius.');
      return;
    }

    setError('');
    setLoading(true);
    try {
      const startDate = startDateLocal ? new Date(startDateLocal).toISOString() : undefined;
      const endDate = endDateLocal ? new Date(endDateLocal).toISOString() : undefined;
      const res = await axios.post(`${API_URL}/api/admin/area-reports`, {
        title,
        centerLat: numericInputs.lat,
        centerLng: numericInputs.lng,
        radiusMeters: numericInputs.radius,
        startDate,
        endDate
      }, {
        headers: getAuthHeaders()
      });

      const id = res.data?.id;
      await fetchSavedReports();
      if (id) {
        setSearchParams({ id });
      }
    } catch (err) {
      if (err.response?.status === 401 || err.response?.status === 403) {
        adminLogout();
        navigate('/admin/login');
        return;
      }
      setError(err.response?.data?.message || 'Failed to create report');
    } finally {
      setLoading(false);
    }
  };

  const openPrintableView = () => {
    if (!selectedSaved?.html) return;
    const w = window.open('', '_blank');
    if (!w) {
      setError('Popup blocked. Please allow popups to print the report.');
      return;
    }
    w.document.open();
    w.document.write(selectedSaved.html);
    w.document.close();
    w.focus();
  };

  const buttonStyle = {
    padding: '10px 14px',
    borderRadius: '10px',
    fontSize: '13px',
    fontWeight: 700,
    minHeight: '38px',
    width: 'auto'
  };

  return (
    <div className="admin-dashboard" style={{ padding: '20px' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '12px', marginBottom: '16px' }}>
        <div>
          <h2 style={{ margin: 0 }}>Area Reports</h2>
          <div style={{ color: '#666', fontSize: '13px', marginTop: '4px' }}>
            Generate a printable/shareable report for a coordinate + radius (ideal for government outreach).
          </div>
        </div>
        <button className="action-btn" onClick={() => navigate('/admin/dashboard')} style={{ ...buttonStyle, background: '#6c757d' }}>
          ← Back to Dashboard
        </button>
      </div>

      {error && (
        <div className="error-message" style={{ marginBottom: '12px' }}>{error}</div>
      )}

      <div style={{ display: 'grid', gridTemplateColumns: 'minmax(420px, 520px) 1fr', gap: '14px', alignItems: 'start' }}>
        <div className="reports-table-container" style={{ padding: '14px', borderRadius: '12px' }}>
          <div style={{ fontWeight: 800, marginBottom: '10px' }}>Wizard</div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: '10px' }}>
            <div>
              <label style={{ fontSize: '12px', color: '#555' }}>Title</label>
              <input value={title} onChange={(e) => setTitle(e.target.value)} style={{ width: '100%', padding: '8px 10px', borderRadius: '8px', border: '1px solid #e0e0e0' }} />
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
              <div>
                <label style={{ fontSize: '12px', color: '#555' }}>Center latitude</label>
                <input value={centerLat} onChange={(e) => setCenterLat(e.target.value)} placeholder="e.g. 37.7749" style={{ width: '100%', padding: '8px 10px', borderRadius: '8px', border: '1px solid #e0e0e0' }} />
              </div>
              <div>
                <label style={{ fontSize: '12px', color: '#555' }}>Center longitude</label>
                <input value={centerLng} onChange={(e) => setCenterLng(e.target.value)} placeholder="e.g. -122.4194" style={{ width: '100%', padding: '8px 10px', borderRadius: '8px', border: '1px solid #e0e0e0' }} />
              </div>
            </div>

            <div>
              <label style={{ fontSize: '12px', color: '#555' }}>City / Location (optional)</label>
              <div style={{ display: 'flex', gap: '8px', alignItems: 'stretch' }}>
                <input value={locationQuery} onChange={(e) => setLocationQuery(e.target.value)} placeholder="e.g., San Francisco" style={{ flex: 1, width: '100%', padding: '8px 10px', borderRadius: '8px', border: '1px solid #e0e0e0', boxSizing: 'border-box' }} />
                <button className="action-btn" onClick={handleGeocode} disabled={loading} style={{ ...buttonStyle, background: '#97AC82', whiteSpace: 'nowrap', minWidth: '96px', boxSizing: 'border-box' }}>
                  {loading ? 'Working…' : 'Find'}
                </button>
              </div>
              <div style={{ fontSize: '12px', color: '#777', marginTop: '6px' }}>
                Tip: This will fill the center latitude/longitude automatically.
              </div>
            </div>

            <div>
              <label style={{ fontSize: '12px', color: '#555' }}>Radius (meters)</label>
              <input value={radiusMeters} onChange={(e) => setRadiusMeters(e.target.value)} placeholder="1000" style={{ width: '100%', padding: '8px 10px', borderRadius: '8px', border: '1px solid #e0e0e0' }} />
              <div style={{ fontSize: '12px', color: '#777', marginTop: '6px' }}>
                Tip: 1000m = 1km. 5000m = 5km.
              </div>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
              <div>
                <label style={{ fontSize: '12px', color: '#555' }}>Start date (optional)</label>
                <input type="datetime-local" value={startDateLocal} onChange={(e) => setStartDateLocal(e.target.value)} style={{ width: '100%', padding: '8px 10px', borderRadius: '8px', border: '1px solid #e0e0e0' }} />
              </div>
              <div>
                <label style={{ fontSize: '12px', color: '#555' }}>End date (optional)</label>
                <input type="datetime-local" value={endDateLocal} onChange={(e) => setEndDateLocal(e.target.value)} style={{ width: '100%', padding: '8px 10px', borderRadius: '8px', border: '1px solid #e0e0e0' }} />
              </div>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px', marginTop: '6px' }}>
              <button className="action-btn" onClick={handlePreview} disabled={loading} style={{ ...buttonStyle, background: '#97AC82', width: '100%' }}>
                {loading ? 'Working…' : 'Preview'}
              </button>
              <button className="action-btn" onClick={handleCreate} disabled={loading} style={{ ...buttonStyle, background: '#2C341B', width: '100%' }}>
                {loading ? 'Working…' : 'Generate & Save'}
              </button>
            </div>
          </div>

          {preview && (
            <div style={{ marginTop: '14px', borderTop: '1px solid #eee', paddingTop: '12px' }}>
              <div style={{ fontWeight: 800, marginBottom: '8px' }}>Preview</div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
                <div style={{ border: '1px solid #eee', borderRadius: '10px', padding: '10px' }}>
                  <div style={{ fontSize: '12px', color: '#777' }}>Total reports</div>
                  <div style={{ fontSize: '22px', fontWeight: 900 }}>{preview.totalReports || 0}</div>
                </div>
                <div style={{ border: '1px solid #eee', borderRadius: '10px', padding: '10px' }}>
                  <div style={{ fontSize: '12px', color: '#777' }}>Types</div>
                  <div style={{ fontSize: '22px', fontWeight: 900 }}>{Object.keys(preview.byType || {}).length}</div>
                </div>
              </div>
            </div>
          )}
        </div>

        <div className="reports-table-container" style={{ padding: '14px', borderRadius: '12px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '12px' }}>
            <div style={{ fontWeight: 800 }}>Saved reports</div>
            <button className="action-btn" onClick={fetchSavedReports} disabled={loading} style={{ ...buttonStyle, background: '#6c757d' }}>
              Refresh
            </button>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'minmax(300px, 380px) 1fr', gap: '12px', marginTop: '12px', alignItems: 'start' }}>
            <div style={{ maxHeight: '65vh', overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: '10px' }}>
              {(savedReports || []).length === 0 ? (
                <div className="no-reports">No saved area reports yet</div>
              ) : (
                savedReports.map(r => (
                  <button
                    key={r.id}
                    className="action-btn"
                    onClick={() => setSearchParams({ id: r.id })}
                    style={{
                      textAlign: 'left',
                      background: reportId === r.id ? '#f3f7ef' : '#fff',
                      color: '#333',
                      border: reportId === r.id ? '1px solid #97AC82' : '1px solid #eee',
                      borderRadius: '12px',
                      padding: '10px 10px',
                      width: '100%'
                    }}
                  >
                    <div style={{ fontWeight: 800 }}>{r.title || 'Area Safety Report'}</div>
                    <div style={{ fontSize: '12px', color: '#666', marginTop: '4px' }}>
                      Center: {Number(r.centerLat).toFixed(5)}, {Number(r.centerLng).toFixed(5)}
                    </div>
                    <div style={{ fontSize: '12px', color: '#777' }}>
                      Radius: {Math.round(Number(r.radiusMeters) || 0)}m • {r.createdAt ? new Date(r.createdAt).toLocaleString() : ''}
                    </div>
                  </button>
                ))
              )}
            </div>

            <div style={{ minHeight: '320px' }}>
              {!selectedSaved ? (
                <div className="no-reports">Select a saved report to view/print</div>
              ) : (
                <>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '10px', marginBottom: '10px' }}>
                    <div>
                      <div style={{ fontWeight: 900, fontSize: '16px' }}>{selectedSaved.title || 'Area Safety Report'}</div>
                      <div style={{ fontSize: '12px', color: '#666', marginTop: '2px' }}>
                        Generated: {selectedSaved.createdAt ? new Date(selectedSaved.createdAt).toLocaleString() : ''}
                      </div>
                    </div>
                    <button className="action-btn" onClick={openPrintableView} style={{ background: '#2C341B' }}>
                      Open printable view
                    </button>
                  </div>

                  <div style={{ border: '1px solid #eee', borderRadius: '12px', overflow: 'hidden', background: '#fff' }}>
                    <iframe
                      title="Area report preview"
                      style={{ width: '100%', height: '62vh', border: 0 }}
                      srcDoc={selectedSaved.html || '<html><body>No HTML</body></html>'}
                    />
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminAreaReports;
